import { useState } from 'react';
import {
  computeDateRange,
  DATE_RANGE_PRESET_OPTIONS,
  derivePreset,
} from '../../lib/date-range-presets.js';
import type { DateRangePreset } from '../../lib/date-range-presets.js';

/** @graph-connects none */
interface DateRangeFilterProps {
  from: string;
  to: string;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  minDate?: string;
  fromLabel?: string;
  toLabel?: string;
  defaultPreset?: DateRangePreset;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Date range filter with DOMO-style preset dropdown and custom date inputs.
 * @graph-connects computeDateRange [calls] compute from/to for preset
 * @graph-connects DATE_RANGE_PRESET_OPTIONS [references] preset option list
 * @graph-connects derivePreset [calls] derive initial preset from from/to
 */
export function DateRangeFilter({
  from,
  to,
  onFromChange,
  onToChange,
  minDate,
  fromLabel = '開始日',
  toLabel = '終了日',
  defaultPreset,
}: DateRangeFilterProps) {
  const [preset, setPreset] = useState<DateRangePreset>(
    () => defaultPreset ?? derivePreset(from, to, minDate),
  );

  const handlePresetChange = (newPreset: DateRangePreset): void => {
    setPreset(newPreset);
    if (newPreset !== 'custom') {
      const range = computeDateRange(newPreset, new Date(), minDate);
      onFromChange(range.from);
      onToChange(range.to);
    }
  };

  const handleFromChange = (value: string): void => {
    setPreset('custom');
    onFromChange(value);
  };

  const handleToChange = (value: string): void => {
    setPreset('custom');
    onToChange(value);
  };

  const inputClass =
    'rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-1.5 text-sm text-[var(--color-text-primary)]';

  return (
    <div className="flex items-end gap-3">
      <label className="flex flex-col gap-1 text-xs text-[var(--color-text-secondary)]">
        期間
        <select
          value={preset}
          onChange={(e) => {
            handlePresetChange(e.target.value as DateRangePreset);
          }}
          className={inputClass}
        >
          {DATE_RANGE_PRESET_OPTIONS.map((opt) => (
            <option key={opt.key} value={opt.key}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
      {preset === 'custom' && (
        <>
          <label className="flex flex-col gap-1 text-xs text-[var(--color-text-secondary)]">
            {fromLabel}
            <input
              type="date"
              value={from}
              min={minDate}
              onChange={(e) => {
                handleFromChange(e.target.value);
              }}
              className={inputClass}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-[var(--color-text-secondary)]">
            {toLabel}
            <input
              type="date"
              value={to}
              min={from || minDate}
              onChange={(e) => {
                handleToChange(e.target.value);
              }}
              className={inputClass}
            />
          </label>
        </>
      )}
    </div>
  );
}
